import {
  EnvironmentProviders,
  inject,
  Injectable,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { CanActivateFn } from '@angular/router';
import { catchError, from, of, switchMap, throwError } from 'rxjs';
import { AuthApiService } from './auth-api.service';
import { AuthApiError } from './auth.errors';
import { AuthSessionStore } from './auth-session.store';
import {
  AUTH_API_URL,
  AUTH_CONTEXT,
  AUTH_LOGIN_URL,
  AUTH_RENEW_RETRY,
  SKIP_ACCESS_TOKEN,
} from './auth.tokens';
import { AuthBootstrapResponse, AuthFeatureConfig } from './auth.types';

/** Margen antes del vencimiento para renovar con el temporizador. */
const RENEW_MARGIN_MS = 60_000;
/** Si el token vence antes de esto, se renueva antes de mandar la petición. */
const REQUEST_RENEW_MARGIN_MS = 5_000;
/** Límite de `setTimeout`; un valor mayor se dispara de inmediato. */
const MAX_TIMER_MS = 2_147_483_647;
const RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 30_000] as const;

export const authAccessTokenInterceptor: HttpInterceptorFn = (
  request,
  next,
) => {
  const session = inject(AuthSessionStore);
  const coordinator = inject(AuthSessionCoordinator);
  const usesSessionToken =
    !request.context.get(SKIP_ACCESS_TOKEN) &&
    !request.headers.has('Authorization');

  const ready$ =
    usesSessionToken && coordinator.tokenNearExpiry(REQUEST_RENEW_MARGIN_MS)
      ? from(coordinator.renewSession())
      : of(true);

  return ready$.pipe(
    switchMap(renewed => {
      if (!renewed) {
        coordinator.redirectToLogin();
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 401,
              statusText: 'Unauthorized',
              url: request.url,
            }),
        );
      }

      return next(withAccessToken(request, session.accessToken())).pipe(
        catchError((error: unknown) => {
          const status =
            typeof error === 'object' && error && 'status' in error
              ? Number(error.status)
              : 0;
          const canRenew =
            status === 401 &&
            !request.context.get(SKIP_ACCESS_TOKEN) &&
            !request.context.get(AUTH_RENEW_RETRY);

          if (!canRenew) {
            if (status === 401 && !request.context.get(SKIP_ACCESS_TOKEN)) {
              coordinator.redirectToLogin();
            }
            return throwError(() => error);
          }

          return from(coordinator.renewSession()).pipe(
            switchMap(ok => {
              const token = session.accessToken();
              if (!ok || !token) {
                coordinator.redirectToLogin();
                return throwError(() => error);
              }
              return next(
                request.clone({
                  context: request.context.set(AUTH_RENEW_RETRY, true),
                  setHeaders: { Authorization: `Bearer ${token}` },
                }),
              );
            }),
          );
        }),
      );
    }),
  );
};

function withAccessToken<T>(
  request: HttpRequest<T>,
  accessToken: string | null,
): HttpRequest<T> {
  if (
    !accessToken ||
    request.context.get(SKIP_ACCESS_TOKEN) ||
    request.headers.has('Authorization')
  ) {
    return request;
  }
  return request.clone({
    setHeaders: { Authorization: `Bearer ${accessToken}` },
  });
}

@Injectable({ providedIn: 'root' })
export class AuthSessionCoordinator {
  private readonly api = inject(AuthApiService);
  private readonly session = inject(AuthSessionStore);
  private readonly context = inject(AUTH_CONTEXT);
  private readonly loginUrl = inject(AUTH_LOGIN_URL);
  private pendingInitialization: Promise<AuthBootstrapResponse> | null = null;
  private pendingRenewal: Promise<boolean> | null = null;
  private renewalTimer: ReturnType<typeof setTimeout> | null = null;
  /** Cambia al cerrar sesión para cortar reintentos en curso. */
  private sessionGeneration = 0;

  constructor() {
    this.watchWakeUps();
  }

  async login(email: string, password: string): Promise<AuthBootstrapResponse> {
    const bootstrap = await this.api.login(this.context, email, password);
    this.scheduleRenewal();
    return bootstrap;
  }

  initialize(): Promise<AuthBootstrapResponse> {
    const bootstrap = this.session.bootstrap();
    if (this.session.isAuthenticated() && bootstrap) {
      return Promise.resolve(bootstrap);
    }

    if (!this.pendingInitialization) {
      this.pendingInitialization = this.api
        .establishPortalSession(this.context)
        .then((bootstrap) => {
          this.scheduleRenewal();
          return bootstrap;
        })
        .finally(() => {
          this.pendingInitialization = null;
        });
    }
    return this.pendingInitialization;
  }

  /**
   * Reemite el access token con `portal-token`, sin rotar la cookie.
   * Reintenta ante fallos de red, timeout, 5xx o 429.
   * Solo devuelve `false` si el backend rechaza la sesión (401 / 403).
   */
  renewSession(): Promise<boolean> {
    if (this.pendingRenewal) return this.pendingRenewal;

    const generation = this.sessionGeneration;
    this.pendingRenewal = this.withRenewLock(() =>
      this.renewWithRetry(generation),
    ).finally(() => {
      this.pendingRenewal = null;
    });

    return this.pendingRenewal;
  }

  tokenNearExpiry(marginMs: number): boolean {
    return (
      Boolean(this.session.accessToken()) &&
      this.session.expiresAt() - Date.now() < marginMs
    );
  }

  async logoutAndRedirect(): Promise<void> {
    this.endSession();
    await this.api.logout();
    redirectToLogin(this.loginUrl);
  }

  redirectToLogin(reason?: 'forbidden'): void {
    this.endSession();
    this.session.clear();
    redirectToLogin(this.loginUrl, reason);
  }

  private async renewWithRetry(generation: number): Promise<boolean> {
    for (let attempt = 0; ; attempt++) {
      if (generation !== this.sessionGeneration) return false;
      try {
        await this.api.reissueToken(this.context);
        if (generation !== this.sessionGeneration) return false;
        this.scheduleRenewal();
        return true;
      } catch (error) {
        const authError = error instanceof AuthApiError ? error : null;
        if (authError?.kind === 'unauthorized' || authError?.kind === 'forbidden') {
          return false;
        }
        const wait =
          authError?.kind === 'rate-limit' && authError.retryAfterSeconds
            ? authError.retryAfterSeconds * 1000
            : RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
        await waitForRetry(wait);
      }
    }
  }

  private scheduleRenewal(): void {
    this.clearRenewal();
    const renewAt = this.session.expiresAt() - RENEW_MARGIN_MS;
    const delay = Math.min(
      Math.max(renewAt - Date.now(), 0),
      MAX_TIMER_MS,
    );
    this.renewalTimer = setTimeout(() => this.renewOrLogout(), delay);
  }

  private renewOrLogout(): void {
    void this.renewSession().then(ok => {
      if (!ok) this.redirectToLogin();
    });
  }

  /** Pestaña oculta o equipo suspendido: el temporizador pudo no dispararse. */
  private watchWakeUps(): void {
    if (typeof window === 'undefined') return;
    const check = () => {
      if (!this.tokenNearExpiry(RENEW_MARGIN_MS)) return;
      this.renewOrLogout();
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
    window.addEventListener('focus', check);
    window.addEventListener('online', check);
  }

  /** Evita que varias pestañas de la misma app renueven a la vez. */
  private withRenewLock(task: () => Promise<boolean>): Promise<boolean> {
    const locks =
      typeof navigator !== 'undefined' ? navigator.locks : undefined;
    return locks
      ? locks
          .request(`dyg-auth-renew-${this.context}`, task)
          .then(result => result)
      : task();
  }

  private endSession(): void {
    this.sessionGeneration++;
    this.clearRenewal();
  }

  private clearRenewal(): void {
    if (this.renewalTimer) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
  }
}

export function provideAuth(config: AuthFeatureConfig): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: AUTH_API_URL, useValue: config.apiUrl },
    { provide: AUTH_CONTEXT, useValue: config.context },
    { provide: AUTH_LOGIN_URL, useValue: config.loginUrl },
    provideHttpClient(withInterceptors([authAccessTokenInterceptor])),
  ]);
}

export function providePortalSessionInitialization(): EnvironmentProviders {
  return provideAppInitializer(async () => {
    const coordinator = inject(AuthSessionCoordinator);
    try {
      await coordinator.initialize();
    } catch (error) {
      coordinator.redirectToLogin(
        error instanceof AuthApiError && error.kind === 'forbidden'
          ? 'forbidden'
          : undefined,
      );
    }
  });
}

export const authGuard: CanActivateFn = async () => {
  const coordinator = inject(AuthSessionCoordinator);
  try {
    await coordinator.initialize();
    return true;
  } catch (error) {
    coordinator.redirectToLogin(
      error instanceof AuthApiError && error.kind === 'forbidden'
        ? 'forbidden'
        : undefined,
    );
    return false;
  }
};

/** Espera el tiempo indicado, o menos si vuelve la conexión. */
function waitForRetry(ms: number): Promise<void> {
  if (typeof window === 'undefined') {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  return new Promise(resolve => {
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    window.addEventListener('online', done, { once: true });
  });
}

function redirectToLogin(authUrl: string, reason?: 'forbidden'): void {
  if (typeof window === 'undefined') return;
  const normalized = authUrl.replace(/\/+$/, '');
  const loginUrl = normalized.endsWith('/login')
    ? normalized
    : `${normalized}/login`;
  window.location.assign(reason ? `${loginUrl}?reason=${reason}` : loginUrl);
}
